import { Link } from "react-router-dom";

import { PAGES_LIST } from "../../shared/utils/constants";
import { snakeToTitle } from "../../shared/utils/TextUtil";
import { UserPermission } from "../../dtos/DashboardUserDTO";
import { getFormattedCurrency } from "../../shared/utils/Global";
import { getFormattedDate, objectKeys } from "../../shared/utils/DateUtil";
import { LOAN_DETAIL_FIELDS } from "../../services/LoanApplication/constants";
import { accessControl } from "@unified-dashboard/src/core/access-control";

// LoanDetailsAttributes
export function getFormattedSectionData(
  details: any,
  sourceEntityID = "",
  userID = "",
  permissions: UserPermission,
  pageConfig,
  defaultConfig
) {
  /* The hidden keys are the keys defined in the page config  + the keys defined in default config (globally hidden fields)
   * - the keys defined in showKeys (keys specific to platforms)
   */
  const hiddenKeys = new Set(
    [
      ...(pageConfig?.hideKeys ?? []),
      ...(defaultConfig?.hideKeys ?? []),
    ].filter((key) => !(pageConfig?.showKeys ?? []).includes(key))
  );
  let items = objectKeys(LOAN_DETAIL_FIELDS)
    .filter((field) => !hiddenKeys.has(field))
    .map((key) => {
      let itemValue = details[key];
      let itemMeta = LOAN_DETAIL_FIELDS[key];
      if (itemMeta?.isExcludeEmpty && !itemValue) {
        return {};
      }

      if (key == "offerAmount" && itemValue == "-1") {
        return {};
      }
      if (["createdAt", "updatedAt"].includes(key)) {
        return {
          name: itemMeta.label,
          value: getFormattedDate(itemValue) || "-",
          key,
          category: itemMeta.category,
        };
      }
      if (["cibil", "experian", "cibilHardPull"].includes(key)) {
        let bureauData = details.bureauScores?.find(
          (b) =>
            b.bureauName === key.toUpperCase() ||
            b.bureauName === LOAN_DETAIL_FIELDS?.[key]?.key
        );

        if (bureauData) {
          return {
            name: LOAN_DETAIL_FIELDS?.[key]?.label ?? "",
            value: bureauData?.bureauScore > 0 ? bureauData?.bureauScore : 0,
            key: key,
            category: itemMeta.category,
          };
        }
      }

      if (key === "scheme" && !itemValue) {
        return {};
      }

      if (key === "fis") {
        return {
          key,
          name: "FIS Score",
          category: itemMeta.category,
          value: details?.fisScore ?? "-",
        };
      }

      if (["cibilStatus", "experianStatus"].includes(key)) {
        let bureauData = details.bureauScores?.find(
          (b) => b.bureauName === key.replace(/Status/g, "").toUpperCase()
        );
        if (bureauData) {
          return {
            name: LOAN_DETAIL_FIELDS?.[key]?.label ?? "",
            value: snakeToTitle(bureauData.bureauStatus),
            key: key,
            category: itemMeta.category,
          };
        }
      }

      if (key === "loanType") {
        itemValue = snakeToTitle(itemValue);
      }

      if (key === "customerID" && userID && sourceEntityID) {
        itemValue = accessControl.can(
          { page: PAGES_LIST.LEAD_DETAILS_PAGE, entityIds: sourceEntityID },
          permissions
        ) ? (
          <Link
            className="underline underline-offset-2 text-indigo-700"
            to={`/leads/details/${sourceEntityID}/${
              details.programID || "default"
            }/${userID}`}
          >
            {itemValue}
          </Link>
        ) : (
          itemValue
        );
      }

      if (key === "reqLoanAmount") {
        itemValue = details.reqLoanAmount
          ? getFormattedCurrency(details.reqLoanAmount)
          : "";
      }

      if (key === "processingFee") {
        itemValue = details.processingFee
          ? getFormattedCurrency(details.processingFee)
          : 0;
      }

      return {
        key,
        name: itemMeta.label,
        value: itemValue || "-",
        category: itemMeta.category,
      };
    });

  let data: any = {};
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    let categoryData = data?.[item.category] || [];
    categoryData.push(item);
    data[item.category] = categoryData;
  }

  return data;
}
